/**
 * `bulk-apply` — the Calendar "Bulk edit / delete" modal's include-set and
 * apply plan, as pure functions.
 *
 * Since 2026-09-02 `POST /api/slots/parse-bulk` returns selector RULES plus the
 * per-slot rows the server expanded them into (`ops[i].rule_index` points at
 * its rule). Two consequences this module carries:
 *
 *  - A large rule (`requires_ack`) starts UNTICKED — 162 January deletes must
 *    be acknowledged as a set, not arrive pre-ticked under a preview the
 *    operator scrolls past. "Include all N" is {@link includeRule}.
 *  - Deletes go through ONE all-or-nothing `POST /api/data-row/slots`
 *    `{ids, mode: "delete"}` call ({@link runBatchDelete}), never a per-row
 *    loop and never a cascade: the modal used to cascade-delete a booked slot
 *    (cancelling its bookings unannounced), and a per-row loop files N audit
 *    rows and can stop half-way. A booked slot arrives from the parser as a
 *    `blocked` row; if one still reaches the batch, the server rejects the
 *    whole batch naming it (`result.blockedId`) and the loop resubmits the
 *    rest — bounded, so a pathological batch cannot spin.
 *
 * No React, no I/O: the HTTP call is injected.
 */

import type { BulkRuleSummary, BulkSlotOp, DataRowDeleteResponse } from "./api";

/** Per-slot verdict of a batch delete. */
export interface BatchDeleteOutcome {
  readonly ok: boolean;
  readonly error?: string;
}

export interface BatchDeleteResult {
  /** Keyed by slot id; every id passed in gets exactly one entry. */
  readonly outcomes: Map<string, BatchDeleteOutcome>;
  /** Set when the batch as a whole failed — "Nothing was deleted — <reason>". */
  readonly batchError: string | null;
}

export interface BulkApplyPlan {
  /** Slot ids of the included, applicable `delete` rows, in op order. */
  readonly deletes: ReadonlyArray<string>;
  /** Included, applicable non-delete rows, in op order. */
  readonly edits: ReadonlyArray<BulkSlotOp>;
}

/** Wording for a slot the server refused to delete inside a batch. */
export const BLOCKED_BY_BOOKING = "has a live booking — cancel it by hand";

/** Most `deleteMany` calls one {@link runBatchDelete} may make. */
export const DEFAULT_MAX_PASSES = 3;

function isApplicable(op: BulkSlotOp): boolean {
  return op.blocked === null;
}

/**
 * The include-set a fresh preview opens with: every applicable row EXCEPT the
 * rows of a `requires_ack` rule, which start unticked. A blocked row is never
 * included.
 */
export function defaultIncluded(
  ops: ReadonlyArray<BulkSlotOp>,
  rules: ReadonlyArray<BulkRuleSummary>,
): Set<number> {
  const ack = new Set<number>();
  for (const rule of rules) if (rule.requires_ack) ack.add(rule.index);
  const included = new Set<number>();
  ops.forEach((op, i) => {
    if (isApplicable(op) && !ack.has(op.rule_index)) included.add(i);
  });
  return included;
}

/**
 * "Include all N" — adds every applicable row of `ruleIndex` to `included`.
 * Returns a NEW set (React state); the input is not mutated.
 */
export function includeRule(
  included: ReadonlySet<number>,
  ops: ReadonlyArray<BulkSlotOp>,
  ruleIndex: number,
): Set<number> {
  const next = new Set(included);
  ops.forEach((op, i) => {
    if (op.rule_index === ruleIndex && isApplicable(op)) next.add(i);
  });
  return next;
}

/**
 * Partition the included, applicable rows into the ONE batch delete and the
 * sequential edits. Both halves keep op order. An included index that points
 * at a blocked row (or at nothing) is dropped — the checkbox is disabled for
 * blocked rows, so this is belt-and-braces, not a path.
 */
export function planBulkApply(
  ops: ReadonlyArray<BulkSlotOp>,
  included: ReadonlySet<number>,
): BulkApplyPlan {
  const deletes: string[] = [];
  const edits: BulkSlotOp[] = [];
  ops.forEach((op, i) => {
    if (!included.has(i) || !isApplicable(op)) return;
    if (op.op === "delete") deletes.push(op.slot_id);
    else edits.push(op);
  });
  return { deletes, edits };
}

/**
 * The bounded batch-delete loop.
 *
 *  - `ok` → every remaining id ✓. The server skips ids that no longer exist,
 *    so `result.deleted` may be below `ids.length`; the rows are gone either
 *    way and nothing is noted.
 *  - 409 `has_dependents` WITH `result.blockedId` → that id ✗
 *    ({@link BLOCKED_BY_BOOKING}), dropped, and the rest resubmitted — at most
 *    `maxPasses` calls in total.
 *  - 409 without `blockedId`, any other non-ok body, a thrown call, or an
 *    exhausted pass budget → every remaining id ✗ with the code, and
 *    `batchError` set. Each pass is all-or-nothing server-side, so a failed
 *    pass really did delete nothing.
 *
 * Deliberately NO per-id fallback: it would recreate the partial-apply
 * problem and file N audit rows.
 */
export async function runBatchDelete(
  ids: ReadonlyArray<string>,
  deleteMany: (ids: ReadonlyArray<string>) => Promise<DataRowDeleteResponse>,
  maxPasses: number = DEFAULT_MAX_PASSES,
): Promise<BatchDeleteResult> {
  const outcomes = new Map<string, BatchDeleteOutcome>();
  let remaining = [...ids];
  if (remaining.length === 0) return { outcomes, batchError: null };

  const failRemaining = (code: string, reason: string): BatchDeleteResult => {
    for (const id of remaining) outcomes.set(id, { ok: false, error: code });
    return { outcomes, batchError: `Nothing was deleted — ${reason}` };
  };

  for (let pass = 1; pass <= maxPasses; pass += 1) {
    let res: DataRowDeleteResponse;
    try {
      res = await deleteMany(remaining);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      return failRemaining(message, message);
    }
    if (res.ok) {
      for (const id of remaining) outcomes.set(id, { ok: true });
      return { outcomes, batchError: null };
    }
    const code = res.code ?? res.result?.code ?? "delete_failed";
    const blockedId = res.result?.blockedId;
    if (
      code === "has_dependents" &&
      typeof blockedId === "string" &&
      remaining.includes(blockedId)
    ) {
      outcomes.set(blockedId, { ok: false, error: BLOCKED_BY_BOOKING });
      remaining = remaining.filter((id) => id !== blockedId);
      if (remaining.length === 0) return { outcomes, batchError: null };
      continue;
    }
    return failRemaining(code, code);
  }
  const n = remaining.length;
  return failRemaining(
    "batch_pass_budget_exhausted",
    `${n} slot${n === 1 ? "" : "s"} still unattempted after ${maxPasses} blocked passes. Cancel the flagged bookings by hand and apply again.`,
  );
}

/**
 * True when an included, applicable RETIME row moves a slot that has bookings
 * — the only case where the "notify booked members" choice changes anything.
 */
export function needsNotifyChoice(
  ops: ReadonlyArray<BulkSlotOp>,
  included: ReadonlySet<number>,
): boolean {
  return ops.some(
    (op, i) =>
      included.has(i) &&
      isApplicable(op) &&
      op.op === "retime" &&
      op.current.taken_count > 0,
  );
}

/** The Apply button's label while a batch is in flight. */
export function progressLabel(done: number, total: number): string {
  return `Applying ${done}/${total}…`;
}
